import { ArrowTopRightOnSquareIcon, ArrowUturnLeftIcon, ChevronRightIcon, XMarkIcon } from "@heroicons/react/24/outline";
import React from "react";

const FraudFaqs: React.FC = () => {
  const linkItems = [
    { text: "What is online fraud?", icon: ChevronRightIcon, target: "what-is-online-fraud" },
    { text: "What is payment fraud?", icon: ChevronRightIcon, target: "what-is-payment-fraud" },
    { text: "What is APP fraud and how do I avoid it?", icon: ChevronRightIcon, target: "what-is-app-fraud-and-how-do-i-avoid-it" },
    { text: "What are the most common types of payment fraud?", icon: ChevronRightIcon, target: "what-are-the-most-common-types-of-payment-fraud" },
    { text: "What are the most common types of scams?", icon: ChevronRightIcon, target: "what-are-the-most-common-types-of-scams" },
    { text: "How can I protect myself from fraud?", icon: ChevronRightIcon, target: "how-can-i-protect-myself-from-fraud" },
    { text: "What is contactless card fraud, and how can I prevent it?", icon: ChevronRightIcon, target: "what-is-contactless-card-fraud-and-how-can-i-prevent-it" },
    { text: "What is phishing?", icon: ChevronRightIcon, target: "what-is-phishing" },
    { text: "What is two-Factor Authentication (2FA)?", icon: ChevronRightIcon, target: "what-is-two-factor-authentication-2fa" },
    { text: "What should I do if I suspect fraud on my account?", icon: ChevronRightIcon, target: "what-should-i-do-if-i-suspect-fraud-on-my-account" },
    { text: "What should I do if my card is lost or stolen?", icon: ChevronRightIcon, target: "what-should-i-do-if-my-card-is-lost-or-stolen" },
    { text: "What should I do if I am a victim of fraud or cybercrime?", icon: ChevronRightIcon, target: "what-should-i-do-if-i-am-a-victim-of-fraud-or-cybercrime" },
    { text: "Where can I find more information about fraud prevention?", icon: ChevronRightIcon, target: "where-can-i-find-more-information-about-fraud-prevention" },
  ];

  return (
    <div className="flex flex-col items-center container pt-20">
      {/* Header Section */}
      <header className="w-full px-5">
        <h1 className="text-4xl font-extrabold text-gray-900">Fraud FAQs</h1>
        <p className="mt-4 text-lg text-gray-700">This Faq was last updated on 24 May 2025.  </p>
      </header>

      {/* Main Content Section */}
      <section className="w-full px-5 mt-8 p-6">
        <p className="text-gray-800">If you have any questions about how we use your account, you can get in touch by one of the methods set out in the Contact us section.</p>
      </section>

      {/* Link Section */}
      <div className="w-full mt-8 bg-gray-100 rounded-lg p-6">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {linkItems.map((item, index) => {
            const IconComponent = item.icon; // Get the icon component dynamically
            return (
              <a
                key={index}
                href={`#${item.target}`} // Anchor link to the section
                className="flex items-center justify-between bg-white p-4 rounded-lg shadow-md hover:bg-gray-50 transition"
              >
                <span className="text-gray-900 font-medium">{item.text}</span>
                <IconComponent className="h-6 w-6" /> {/* Render the icon dynamically */}
              </a>
            );
          })}
        </div>
      </div>

      {/* Additional Fraud FAQ Sections */}
      <section id="what-is-online-fraud" className="w-full px-5 my-8 scroll-mt-20">
        <h3 className="text-2xl my-4">What is online fraud?</h3>
        <p className="text-gray-800">
          Online fraud is any type of fraudulent activity that uses the internet. This includes scams designed to steal your money, personal information, or both, through websites, emails, social media, and other online platforms.
        </p>
      </section>
      <section id="what-is-payment-fraud" className="w-full px-5 my-8 scroll-mt-20">
        <h3 className="text-2xl my-4">What is payment fraud?</h3>
        <p className="text-gray-800">
          Payment fraud is a serious issue that affects individuals and businesses alike. It involves any type of fraudulent or illegal activity where someone uses stolen or fake payment information to obtain goods, services, or money.
        </p>
      </section>
      <section id="what-is-app-fraud-and-how-do-i-avoid-it" className="w-full px-5 my-8 scroll-mt-20">
        <h3 className="text-2xl my-4">What is APP fraud and how do I avoid it?</h3>
        <p className="text-gray-800">APP (Authorised Push Payment) fraud is when criminals trick you into voluntarily sending money to their account by pretending to be a legitimate person or organisation. To avoid it:</p>
        <ul className="list-disc ml-6 text-gray-800">
          <li>Verify the recipient's details before sending money.</li>
          <li>Be wary of urgent requests for money.</li>
          <li>Don't be pressured into making a payment.</li>
          <li>If a deal seems too good to be true, it likely is a scam.</li>
          <li>If you are asked to send money to a new account, verify that account before sending funds.</li>
        </ul>
      </section>
      <section id="what-are-the-most-common-types-of-payment-fraud" className="w-full px-5 my-8 scroll-mt-20">
        <h3 className="text-2xl my-4">What are the most common types of payment fraud?</h3>
        <ul className="list-disc ml-6 text-gray-800">
          <li>Phishing: Fraudulent emails, texts, or calls tricking you into revealing personal or financial information.</li>
          <li>Skimming: Criminals illegally copying card data at ATMs or point-of-sale terminals.</li>
          <li>Account Takeover: Criminals gaining access to your online banking or e-money account.</li>
          <li>Malware: Malicious software that steals your data.</li>
          <li>Authorised Push Payment (APP) Fraud: Tricking you into sending money to a criminal.</li>
        </ul>
      </section>
      <section id="what-are-the-most-common-types-of-scams" className="w-full px-5 my-8 scroll-mt-20">
        <h3 className="text-2xl my-4">What are the most common types of scams?</h3>
        <ul className="list-disc ml-6 text-gray-800">
          <li>Romance scams: The fraudster preys on the victim's need for affection by establishing an online romantic relationship to later persuade them to send money.</li>
          <li>Investment scams: Fake investment websites promising high returns with little or no risk.</li>
          <li>Advance fee scams: Victims are told they must pay fees to receive a large sum of money, such as lottery winnings or inheritance.</li>
          <li>CEO scams: Fraudsters pose as a boss or senior manager to convince employees to make urgent payments or change payment details.</li>
          <li>Mule accounts: Individuals are convinced to act as 'money mules' to transfer fraudulently obtained funds.</li>
          <li>Gift card scams: Victims are instructed to purchase gift cards and provide the card numbers and PINs to scammers.</li>
          <li>Deepfake: Criminals use software to fake someone's voice for fraudulent purposes.</li>
          <li>Employment scams: Fake job opportunities requiring payment for applications, checks, or training.</li>
        </ul>
      </section>
      <section id="how-can-i-protect-myself-from-fraud" className="w-full px-5 my-8 scroll-mt-20">
        <h3 className="text-2xl my-4">How can I protect myself from fraud?</h3>
        <ul className="list-disc ml-6 text-gray-800">
          <li>Never share your PIN, passwords, card details, or CVV/CVC code.</li>
          <li>Never share personal documents like passports, national IDs, or driving licenses.</li>
          <li>Be cautious of unsolicited emails, texts, or calls asking for personal information.</li>
          <li>Use strong, unique passwords for all your online accounts.</li>
          <li>Regularly check your account statements.</li>
          <li>Keep your devices, operating systems, antivirus, and anti-malware software up to date.</li>
          <li>Be wary of suspicious websites and online offers.</li>
          <li>Research online stores before making purchases to ensure legitimacy.</li>
          <li>Enable two-factor authentication (2FA) wherever possible.</li>
          <li>Only use trusted Wi-Fi networks when using your accounts or making purchases.</li>
          <li>Be sceptical of anyone asking you to transfer money urgently.</li>
        </ul>
      </section>
      <section id="what-is-contactless-card-fraud-and-how-can-i-prevent-it" className="w-full px-5 my-8 scroll-mt-20">
        <h3 className="text-2xl my-4">What is contactless card fraud, and how can I prevent it?</h3>
        <p className="text-gray-800">Contactless card fraud occurs when criminals use stolen cards or devices to make small, unauthorized contactless payments. To prevent it:</p>
        <ul className="list-disc ml-6 text-gray-800">
          <li>Regularly check your statements for unauthorized transactions.</li>
          <li>Consider setting a limit on contactless payments.</li>
          <li>Keep your card safe and secure.</li>
          <li>If your card is lost or stolen, report it immediately.</li>
        </ul>
      </section>
      <section id="what-is-phishing" className="w-full px-5 my-8 scroll-mt-20">
        <h3 className="text-2xl my-4">What is phishing?</h3>
        <p className="text-gray-800">
          Phishing involves criminals sending fraudulent messages that appear to be from your payment services provider. These messages often ask for your login credentials, card credentials, personal information, or to click on malicious links.
        </p>
      </section>
      <section id="what-is-two-factor-authentication-2fa" className="w-full px-5 my-8 scroll-mt-20">
        <h3 className="text-2xl my-4">What is two-Factor Authentication (2FA)?</h3>
        <p className="text-gray-800">
          2FA gives you twice the protection over your emails and social media accounts, which are common entry points that fraudsters try to exploit to get access to your e-money accounts and cards. Even if cyber criminals have your password, they
          can't access your account without the second factor, such as a code sent to your mobile phone when signing in from a new device or changing settings.
        </p>
      </section>
      <section id="what-should-i-do-if-i-suspect-fraud-on-my-account" className="w-full px-5 my-8 scroll-mt-20">
        <h3 className="text-2xl my-4">What should I do if I suspect fraud on my account?</h3>
        <ul className="list-disc ml-6 text-gray-800">
          <li>Immediately contact us.</li>
          <li>Change your passwords and PINs.</li>
          <li>Report the incident to the police or relevant authorities.</li>
          <li>Keep a record of all communication and actions taken.</li>
        </ul>
      </section>
      <section id="what-should-i-do-if-my-card-is-lost-or-stolen" className="w-full px-5 my-8 scroll-mt-20">
        <h3 className="text-2xl my-4">What should I do if my card is lost or stolen?</h3>
        <ul className="list-disc ml-6 text-gray-800">
          <li>Immediately report the loss or theft to us.</li>
          <li>Request a new card.</li>
          <li>Check your account for any unauthorized transactions.</li>
        </ul>
      </section>
      <section id="what-should-i-do-if-i-am-a-victim-of-fraud-or-cybercrime" className="w-full px-5 my-8 scroll-mt-20">
        <h3 className="text-2xl my-4">What should I do if I am a victim of fraud or cybercrime?</h3>
        <p className="text-gray-800">
          For card and account holders living in England, Wales and Northern Ireland who have been a victim of fraud or cybercrime, report it at{" "}
          <a href="https://www.actionfraud.police.uk" className="text-blue-600 underline" target="_blank" rel="noopener noreferrer">
            www.actionfraud.police.uk
          </a>{" "}
          or by calling 0300 123 2040. In Scotland, report to Police Scotland on 101.
          <br />
          In Malta, customers can report fraud to the Cyber Crime Unit, Malta Police Force, telephone no. +356 2294 2231, or email:{" "}
          <a href="mailto:computer.crime@gov.mt" className="text-blue-600 underline">
            computer.crime@gov.mt
          </a>
        </p>
      </section>
      <section id="where-can-i-find-more-information-about-fraud-prevention" className="w-full px-5 my-8 scroll-mt-20">
        <h3 className="text-2xl my-4">Where can I find more information about fraud prevention?</h3>
        <ul className="list-disc ml-6 text-gray-800">
          <li>
            <a href="https://www.europol.europa.eu/crime-areas-and-statistics/crime-areas/financial-crime/payment-fraud" className="text-blue-600 underline" target="_new" rel="noopener noreferrer">
              Europol’s website - Payment Fraud
            </a>
          </li>
          <li>
            <a href="https://stopscamsuk.org.uk/" className="text-blue-600 underline" target="_new" rel="noopener noreferrer">
              Stop Scams UK
            </a>
          </li>
          <li>
            <a href="https://www.actionfraud.police.uk/" className="text-blue-600 underline" target="_new" rel="noopener noreferrer">
              Action Fraud
            </a>
          </li>
          <li>
            <a href="https://www.ukicc.org.uk/common-scams" className="text-blue-600 underline" target="_new" rel="noopener noreferrer">
              The UK International Consumer Centre - Common Scams
            </a>
          </li>
          <li>
            <a href="https://pulizija.gov.mt/en/police-force/crime-prevention/" className="text-blue-600 underline" target="_new" rel="noopener noreferrer">
              Crime Prevention – Malta Police
            </a>
          </li>
          <li>
            <a href="https://www.eba.europa.eu/consumer-corner/frauds-and-scams" className="text-blue-600 underline" target="_new" rel="noopener noreferrer">
              The European Banking Authority (EBA) website - Frauds and scams
            </a>
          </li>
          <li>National consumer protection agencies.</li>
        </ul>
      </section>
    </div>
  );
};

export default FraudFaqs;
